import { useCallback, useMemo, useState } from "react";

import { AccountBar } from "@/components/AccountBar";
import { Footer } from "@/components/Footer";
import { Masthead } from "@/components/Masthead";
import { PuzzleCard } from "@/components/PuzzleCard";
import { api } from "@/lib/api";
import {
  applyFilters,
  DEFAULT_FILTERS,
  SORT_OPTIONS,
  type PuzzleFilters,
  type SortId,
} from "@/lib/puzzleFilters";
import { puzzlesApi, type Puzzle, type PuzzleList, type RatingDraft } from "@/lib/puzzlesApi";
import { useAccount } from "@/lib/useAccount";
import { useApi } from "@/lib/useApi";

export function Puzzles() {
  const club = useApi((signal) => api.club(signal));
  const { session, loading: accountLoading } = useAccount();
  const memberId = session?.user_id ?? null;

  // Refetched when the signed-in member changes, because the response carries
  // "your rating" per puzzle — a stale list would show the last person's.
  // Held until the session resolves. Fetching first and again on resolution
  // would request the archive twice on every signed-in page view, and the
  // response differs — it carries "your rating" per puzzle.
  const archive = useApi<PuzzleList | null>(
    (signal) => (accountLoading ? Promise.resolve(null) : puzzlesApi.list(signal)),
    [memberId, accountLoading],
  );

  const [overrides, setOverrides] = useState<Record<number, Puzzle>>({});
  const [filters, setFilters] = useState<PuzzleFilters>(DEFAULT_FILTERS);

  // Cleared alongside the refetch above, and for the same reason. These rows
  // carry "your rating", so keeping them across a sign-out would paint the
  // previous member's ratings back over the freshly-fetched list — defeating
  // the refetch rather than complementing it.
  const [overridesFor, setOverridesFor] = useState(memberId);
  if (overridesFor !== memberId) {
    setOverridesFor(memberId);
    setOverrides({});
  }

  const patch = (changes: Partial<PuzzleFilters>) =>
    setFilters((current) => ({ ...current, ...changes }));

  /**
   * Rating one puzzle updates that row in place rather than refetching all 140:
   * a full reload would scroll-jump and lose the reader's position mid-list.
   */
  const rate = useCallback(async (id: number, draft: RatingDraft) => {
    const updated = await puzzlesApi.rate(id, draft);
    setOverrides((current) => ({ ...current, [id]: updated }));
    return updated;
  }, []);

  const unrate = useCallback(async (id: number) => {
    // The endpoint hands back the updated puzzle, so one row is redrawn without
    // refetching the whole archive and losing the reader's place.
    const updated = await puzzlesApi.unrate(id);
    setOverrides((current) => ({ ...current, [id]: updated }));
  }, []);

  const puzzles = useMemo(() => {
    const base = archive.data?.puzzles ?? [];
    return base.map((puzzle) => overrides[puzzle.id] ?? puzzle);
  }, [archive.data, overrides]);

  const visible = useMemo(() => applyFilters(puzzles, filters), [puzzles, filters]);

  const ratedByMe = useMemo(
    () => puzzles.filter((puzzle) => puzzle.my_rating).length,
    [puzzles],
  );

  return (
    <>
      <Masthead variant="page" club={club.data} />

      <main className="lattice min-h-[calc(100vh-64px)] border-b-2 border-ink">
        <div className="shell py-8 md:py-10">
          <div className="mb-6 flex flex-wrap items-baseline gap-x-5 gap-y-1">
            <h1 className="text-[clamp(2rem,5vw,3.25rem)]">Puzzles</h1>
            <span className="meta opacity-70">{puzzles.length} in the archive</span>
          </div>

          <p className="mb-6 max-w-[62ch] leading-relaxed opacity-80">
            Puzzles collected by Tetris At UCI, opened in the blueprint viewer. Each one shows
            two difficulties: the number its creator gave it in the original spreadsheet, and
            what the club has voted since. Both are kept, because the gap between them is
            usually the interesting part.
          </p>

          <div className="mb-8">
            <AccountBar />
          </div>

          {/* ── controls ── */}
          <div className="panel mb-8 grid gap-4 px-5 py-4 md:grid-cols-[1fr_auto_auto]">
            <div>
              <label className="label" htmlFor="puzzle-search">
                Search
              </label>
              <input
                id="puzzle-search"
                className="input"
                placeholder="Title, creator, set, or #number"
                value={filters.search}
                onChange={(event) => patch({ search: event.target.value })}
              />
            </div>

            <div>
              <label className="label" htmlFor="puzzle-set">
                Set
              </label>
              <select
                id="puzzle-set"
                className="input"
                value={filters.set ?? ""}
                onChange={(event) => patch({ set: event.target.value || null })}
              >
                <option value="">All sets</option>
                {(archive.data?.sets ?? []).map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label" htmlFor="puzzle-sort">
                Sort
              </label>
              <select
                id="puzzle-sort"
                className="input"
                value={filters.sort}
                onChange={(event) => patch({ sort: event.target.value as SortId })}
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {session ? (
              <label className="flex items-center gap-2.5 text-[0.9375rem] md:col-span-3">
                <input
                  type="checkbox"
                  checked={filters.unratedOnly}
                  onChange={(event) => patch({ unratedOnly: event.target.checked })}
                />
                Only show puzzles I have not rated
                <span className="meta ml-2 opacity-60">
                  {ratedByMe} of {puzzles.length} rated
                </span>
              </label>
            ) : null}
          </div>

          {/* ── the archive ── */}
          {archive.loading || accountLoading ? (
            <p className="meta opacity-60">Loading the archive…</p>
          ) : archive.error ? (
            <p role="alert" className="panel px-5 py-4 leading-relaxed">
              The archive could not be loaded. Reload the page to try again.
            </p>
          ) : visible.length === 0 ? (
            <p className="panel px-5 py-4 leading-relaxed">
              Nothing matches that. Clear the search or pick a different set.
            </p>
          ) : (
            <>
              <p className="meta mb-4 opacity-60">
                Showing {visible.length} of {puzzles.length}
              </p>
              <div className="grid gap-4 lg:grid-cols-2">
                {visible.map((puzzle) => (
                  <PuzzleCard
                    key={puzzle.id}
                    puzzle={puzzle}
                    onRate={rate}
                    onUnrate={unrate}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      <Footer club={club.data} />
    </>
  );
}
